export const DEMO_LAB_POLL_INTERVAL_MS = 5000;

/** Poll `GET company-portal/processing/:id/stage/` until step 2 finishes. */
export const PORTAL_LAB_STAGE_POLL_INTERVAL_MS = 2000;

export type PortalElementIconName =
    | "navigation"
    | "image"
    | "banner-hero"
    | "liveboard-area"
    | "ai-chat-agent"
    | "kpi"
    | "content-block"
    | "chart";

export type PortalElementListItem = {
    id: string;
    label: string;
    icon: PortalElementIconName;
    iconColor?: string;
    height?: string;
    bgColor?: string;
};

export const PORTAL_ELEMENT_ITEMS: PortalElementListItem[] = [
    { id: "banner-hero", label: "Banner / Hero", icon: "banner-hero", iconColor: "#4275BA", height: "120px", bgColor: "#92B3DB " },
    { id: "liveboard-area", label: "Liveboard Area", icon: "liveboard-area", iconColor: "#7D426E", height: "300px", bgColor: "#AB78A0" },
    { id: "ai-chat-widget", label: "AI Chat Widget", icon: "ai-chat-agent", iconColor: "#B45309", height: "300px", bgColor: "#FCD520" },
    { id: "kpi-block", label: "KPI", icon: "kpi", iconColor: "#23C181", height: "96px", bgColor: "#4FCD9A" },
    { id: "kpi-block-group", label: "KPI Block", icon: "kpi", iconColor: "#23C181", height: "96px", bgColor: "#4FCD9A" },
    { id: "specific-chart", label: "Specific Chart", icon: "chart", iconColor: "#BF96B5", height: "300px", bgColor: "#BF96B5" },
    { id: "content-block", label: "Content Block", icon: "content-block", iconColor: "#32302E", height: "300px", bgColor: "#32302E" },
    { id: "image", label: "Image", icon: "image", iconColor: "#8C857E", height: "300px", bgColor: "#8C857E" },
];

export type PortalElementBlockId = (typeof PORTAL_ELEMENT_ITEMS)[number]["id"];

export type PortalElementType =
    | "navigation_menu"
    | "banner_hero"
    | "liveboard_area"
    | "ai_chat_widget"
    | "kpi_block"
    | "specific_chart"
    | "content_block"
    | "image";

export const PORTAL_KPI_ELEMENT_CONFIG_LIVEBOARD_KEY = "thoughtspot_liveboard";
export const PORTAL_KPI_ELEMENT_CONFIG_KPI_TILE_KEY = "thoughtspot_kpi_tile";
export const PORTAL_SPECIFIC_CHART_ELEMENT_CONFIG_LIVEBOARD_KEY = "thoughtspot_liveboard";
export const PORTAL_SPECIFIC_CHART_ELEMENT_CONFIG_CHART_KEY = "thoughtspot_chart";
/** AI chat widget — selected ThoughtSpot Spotter model from `thoughtspot-spotter-models`. */
export const PORTAL_AI_CHAT_WIDGET_ELEMENT_CONFIG_SPOTTER_MODEL_KEY = "thoughtspot_spotter_model";
export const PORTAL_LIVEBOARD_AREA_ELEMENT_CONFIG_LIVEBOARD_KEY = "thoughtspot_liveboard";
export const PORTAL_BANNER_ELEMENT_CONFIG_URL_KEY = "banner_image_url";
export const PORTAL_BANNER_ELEMENT_CONFIG_HEIGHT_KEY = "banner_image_height";
export const PORTAL_CONTENT_ELEMENT_CONFIG_BODY_KEY = "body";
export const PORTAL_IMAGE_ELEMENT_CONFIG_URL_KEY = "url";

const PORTAL_ELEMENT_TYPE_BY_BLOCK_ID = new Map<string, PortalElementType>([
    ["navigation-menu", "navigation_menu"],
    ["banner-hero", "banner_hero"],
    ["liveboard-area", "liveboard_area"],
    ["ai-chat-widget", "ai_chat_widget"],
    ["kpi-block", "kpi_block"],
    ["specific-chart", "specific_chart"],
    ["content-block", "content_block"],
    ["image", "image"],
]);

const PORTAL_ELEMENT_BLOCK_ID_BY_TYPE = new Map<PortalElementType, PortalElementBlockId | "navigation-menu">(
    Array.from(PORTAL_ELEMENT_TYPE_BY_BLOCK_ID.entries()).map(([blockId, type]) => [
        type,
        blockId as PortalElementBlockId | "navigation-menu",
    ])
);

export const getPortalElementTypeFromBlockId = (blockId: string): PortalElementType =>
    PORTAL_ELEMENT_TYPE_BY_BLOCK_ID.get(blockId) ?? (blockId.replace(/-/g, "_") as PortalElementType);

export const getPortalBlockIdFromElementType = (
    type: string
): PortalElementBlockId | "navigation-menu" =>
    PORTAL_ELEMENT_BLOCK_ID_BY_TYPE.get(type as PortalElementType) ??
    (type.replace(/_/g, "-") as PortalElementBlockId);

export const PORTAL_ELEMENT_BY_ID = new Map(
    PORTAL_ELEMENT_ITEMS.map((e) => [e.id, e])
);

export const PORTAL_CANVAS_HEADLINES: Partial<Record<PortalElementBlockId, string>> = {
    "banner-hero": "BANNER/HERO",
    "liveboard-area": "LIVEBOARD/DASHBOARD",
    "full-chat-agent": "CHAT AGENT",
    "ai-chat-widget": "AICHAT",
    "kpi-block": "KPI",
    "specific-chart": "CHART",
    "content-block": "CONTENT",
    "image": "IMAGE",
};

export interface PortalPageTemplate {
    id: string;
    title: string;
    subtitle: string;
    previewRows: PortalElementBlockId[][];
}

export type PortalCornerRadius = "none" | "s" | "m" | "l";

export const PORTAL_FONT_OPTIONS = [
    {
      id: "abril-fatface",
      name: "Abril Fatface",
      category: "display",
      url: "https://fonts.googleapis.com/css2?family=Abril+Fatface&display=swap"
    },
    {
      id: "aleo",
      name: "Aleo",
      category: "serif",
      url: "https://fonts.googleapis.com/css2?family=Aleo:wght@300;400;500;600;700&display=swap"
    },
    {
      id: "anton",
      name: "Anton",
      category: "display",
      url: "https://fonts.googleapis.com/css2?family=Anton&display=swap"
    },
    {
      id: "archivo",
      name: "Archivo",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Archivo:wght@100;200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "archivo-black",
      name: "Archivo Black",
      category: "display",
      url: "https://fonts.googleapis.com/css2?family=Archivo+Black&display=swap"
    },
    {
      id: "arvo",
      name: "Arvo",
      category: "serif",
      url: "https://fonts.googleapis.com/css2?family=Arvo:wght@400;700&display=swap"
    },
    {
      id: "asap",
      name: "Asap",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Asap:wght@100;200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "bebas-neue",
      name: "Bebas Neue",
      category: "display",
      url: "https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap"
    },
    {
      id: "bitter",
      name: "Bitter",
      category: "serif",
      url: "https://fonts.googleapis.com/css2?family=Bitter:wght@100;200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "cabin",
      name: "Cabin",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Cabin:wght@400;500;600;700&display=swap"
    },
    {
      id: "cairo",
      name: "Cairo",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Cairo:wght@200;300;400;500;600;700;800;900;1000&display=swap"
    },
    {
      id: "cardo",
      name: "Cardo",
      category: "serif",
      url: "https://fonts.googleapis.com/css2?family=Cardo:wght@400;700&display=swap"
    },
    {
      id: "cormorant-garamond",
      name: "Cormorant Garamond",
      category: "serif",
      url: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&display=swap"
    },
    {
      id: "crimson-pro",
      name: "Crimson Pro",
      category: "serif",
      url: "https://fonts.googleapis.com/css2?family=Crimson+Pro:wght@200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "dancing-script",
      name: "Dancing Script",
      category: "handwriting",
      url: "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;500;600;700&display=swap"
    },
    {
      id: "dm-sans",
      name: "DM Sans",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@100;200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "eb-garamond",
      name: "EB Garamond",
      category: "serif",
      url: "https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500;600;700;800&display=swap"
    },
    {
      id: "exo-2",
      name: "Exo 2",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Exo+2:wght@100;200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "figtree",
      name: "Figtree",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Figtree:wght@300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "fira-code",
      name: "Fira Code",
      category: "monospace",
      url: "https://fonts.googleapis.com/css2?family=Fira+Code:wght@300;400;500;600;700&display=swap"
    },
    {
      id: "fira-sans",
      name: "Fira Sans",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Fira+Sans:wght@100;200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "fredoka",
      name: "Fredoka",
      category: "display",
      url: "https://fonts.googleapis.com/css2?family=Fredoka:wght@300;400;500;600;700&display=swap"
    },
    {
      id: "heebo",
      name: "Heebo",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Heebo:wght@100;200;300;400;500;700;800;900&display=swap"
    },
    {
      id: "ibm-plex-sans",
      name: "IBM Plex Sans",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@100;200;300;400;500;600;700&display=swap"
    },
    {
      id: "ibm-plex-serif",
      name: "IBM Plex Serif",
      category: "serif",
      url: "https://fonts.googleapis.com/css2?family=IBM+Plex+Serif:wght@100;200;300;400;500;600;700&display=swap"
    },
    {
      id: "inconsolata",
      name: "Inconsolata",
      category: "monospace",
      url: "https://fonts.googleapis.com/css2?family=Inconsolata:wght@200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "inter",
      name: "Inter",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "jetbrains-mono",
      name: "JetBrains Mono",
      category: "monospace",
      url: "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@100;200;300;400;500;600;700;800&display=swap"
    },
    {
      id: "josefin-sans",
      name: "Josefin Sans",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@100;200;300;400;500;600;700&display=swap"
    },
    {
      id: "karla",
      name: "Karla",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Karla:wght@200;300;400;500;600;700;800&display=swap"
    },
    {
      id: "lato",
      name: "Lato",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Lato:wght@100;300;400;700;900&display=swap"
    },
    {
      id: "libre-baskerville",
      name: "Libre Baskerville",
      category: "serif",
      url: "https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&display=swap"
    },
    {
      id: "lora",
      name: "Lora",
      category: "serif",
      url: "https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&display=swap"
    },
    {
      id: "manrope",
      name: "Manrope",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Manrope:wght@200;300;400;500;600;700;800&display=swap"
    },
    {
      id: "merriweather",
      name: "Merriweather",
      category: "serif",
      url: "https://fonts.googleapis.com/css2?family=Merriweather:wght@300;400;700;900&display=swap"
    },
    {
      id: "montserrat",
      name: "Montserrat",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Montserrat:wght@100;200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "mulish",
      name: "Mulish",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Mulish:wght@200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "nunito",
      name: "Nunito",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Nunito:wght@200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "open-sans",
      name: "Open Sans",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700;800&display=swap"
    },
    {
      id: "oswald",
      name: "Oswald",
      category: "display",
      url: "https://fonts.googleapis.com/css2?family=Oswald:wght@200;300;400;500;600;700&display=swap"
    },
    {
      id: "outfit",
      name: "Outfit",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Outfit:wght@100;200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "pacifico",
      name: "Pacifico",
      category: "handwriting",
      url: "https://fonts.googleapis.com/css2?family=Pacifico&display=swap"
    },
    {
      id: "playfair-display",
      name: "Playfair Display",
      category: "serif",
      url: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&display=swap"
    },
    {
      id: "plus-jakarta-sans",
      name: "Plus Jakarta Sans",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@200;300;400;500;600;700;800&display=swap"
    },
    {
      id: "poppins",
      name: "Poppins",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Poppins:wght@100;200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "quicksand",
      name: "Quicksand",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap"
    },
    {
      id: "raleway",
      name: "Raleway",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Raleway:wght@100;200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "roboto",
      name: "Roboto",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Roboto:wght@100;300;400;500;700;900&display=swap"
    },
    {
      id: "roboto-mono",
      name: "Roboto Mono",
      category: "monospace",
      url: "https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@100;200;300;400;500;600;700&display=swap"
    },
    {
      id: "rubik",
      name: "Rubik",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "source-code-pro",
      name: "Source Code Pro",
      category: "monospace",
      url: "https://fonts.googleapis.com/css2?family=Source+Code+Pro:wght@200;300;400;500;600;700;800;900&display=swap"
    },
    {
      id: "space-grotesk",
      name: "Space Grotesk",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap"
    },
    {
      id: "ubuntu",
      name: "Ubuntu",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Ubuntu:wght@300;400;500;700&display=swap"
    },
    {
      id: "work-sans",
      name: "Work Sans",
      category: "sans-serif",
      url: "https://fonts.googleapis.com/css2?family=Work+Sans:wght@100;200;300;400;500;600;700;800;900&display=swap"
    }
  ] as const;

export type PortalFontOption = (typeof PORTAL_FONT_OPTIONS)[number];

/** Resolve Google Fonts CSS URL from a portal font id or display name. */
export function resolvePortalFontUrl(font: string): string | null {
    const trimmed = font.trim();
    if (!trimmed) {
        return null;
    }

    const option = PORTAL_FONT_OPTIONS.find(
        (opt) => opt.id === trimmed || opt.name === trimmed
    );

    return option?.url ?? null;
}

export const CORNER_RADIUS_SEGMENTS: {
    value: PortalCornerRadius;
    label: string;
    shapeClass: string;
}[] = [
    { value: "none", label: "None", shapeClass: "rounded-none" },
    { value: "s", label: "S", shapeClass: "rounded-sm" },
    { value: "m", label: "M", shapeClass: "rounded-md" },
    { value: "l", label: "L", shapeClass: "rounded-full" },
];

export const PORTAL_DEFAULT_TEMPLATES: PortalPageTemplate[] = [
    {
        id: "executive-overview",
        title: "Executive Overview",
        subtitle: "KPIs + liveboard + chat",
        previewRows: [
            ["banner-hero"],
            ["kpi-block", "kpi-block", "kpi-block", "kpi-block"],
            ["liveboard-area", "ai-chat-widget"],
        ],
    },
    {
        id: "analytics-hub",
        title: "Analytics Hub",
        subtitle: "Full liveboard deep-dive",
        previewRows: [
            ["kpi-block", "kpi-block", "kpi-block"],
            ["liveboard-area"],
            ["ai-chat-widget"]
        ],
    },
    {
        id: "ai-first",
        title: "AI First",
        subtitle: "Conversational data experience",
        previewRows: [
            ["banner-hero"],
            ["ai-chat-widget"]
        ],
    },
    {
        id: "showcase",
        title: "Showcase",
        subtitle: "Media, content + analytics",
        previewRows: [
            ["banner-hero"],
            ["content-block", "ai-chat-widget"],
            ["liveboard-area"],
        ],
    }, 
    {
        id: "data-explorer",
        title: "Data Explorer",
        subtitle: "Self-service exploration",
        previewRows: [
            ["kpi-block", "kpi-block", "liveboard-area"],
            ["banner-hero", "ai-chat-widget"],
        ],
    },
];

const PORTAL_PAGE_TAB_NUMERIC_ID = /^page-(\d+)$/i;
const PORTAL_PAGE_TAB_NUMERIC_NAME = /^Page\s+(\d+)$/i;

function maxPortalLabPageNumberFromIds(pageTabs: readonly { id: string }[]): number {
    let max = 0;
    for (const { id } of pageTabs) {
        const m = PORTAL_PAGE_TAB_NUMERIC_ID.exec(id);
        if (m) {
            const n = Number.parseInt(m[1], 10);
            if (Number.isFinite(n)) {
                max = Math.max(max, n);
            }
        }
    }
    return max;
}

function maxPortalLabPageNumberFromNames(pageTabs: readonly { name: string }[]): number {
    let max = 0;
    for (const { name } of pageTabs) {
        const m = PORTAL_PAGE_TAB_NUMERIC_NAME.exec(name.trim());
        if (m) {
            const n = Number.parseInt(m[1], 10);
            if (Number.isFinite(n)) {
                max = Math.max(max, n);
            }
        }
    }
    return max;
}

export function getNextPortalLabPageTabIdentity(
    pageTabs: readonly { id: string; name: string }[]
): { id: string; name: string } {
    const n =
        Math.max(
            maxPortalLabPageNumberFromIds(pageTabs),
            maxPortalLabPageNumberFromNames(pageTabs)
        ) + 1;
    return { id: `page-${n}`, name: `Page ${n}` };
}